import { useEffect, useId, useRef, useState } from "react"

import { Avatar } from "@coval/ui/avatar"
import { Button } from "@coval/ui/button"
import { Input } from "@coval/ui/input"
import { Label } from "@coval/ui/label"

import { SaveResourceButton } from "../ResourceActionButton"
import { useClerkActions } from "./ClerkActionProvider"

interface ProfileField {
  name: string
  label: string
  value: string
  autoComplete?: string
  optional?: boolean
  readOnly?: boolean
}

export function ProfileEditor({
  name,
  imageUrl,
  hasImage,
  imageLabel = "Photo",
  fields,
  columns = false,
  disabled = false,
  onSave,
  onCancel,
}: {
  name: string
  imageUrl: string
  hasImage: boolean
  imageLabel?: string
  fields: ProfileField[]
  columns?: boolean
  disabled?: boolean
  onSave: (
    values: Record<string, string>,
    image: File | null | undefined,
  ) => Promise<boolean>
  onCancel: () => void
}) {
  const { busy } = useClerkActions()
  const id = useId()
  const upload = useRef<HTMLInputElement>(null)
  const mounted = useRef(true)
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(fields.map((field) => [field.name, field.value])),
  )
  const [image, setImage] = useState<{
    file: File | null
    preview?: string
  } | null>(null)
  const [imageError, setImageError] = useState<string | null>(null)
  const [showValidation, setShowValidation] = useState(false)
  const preview = image?.preview
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])
  useEffect(
    () => () => {
      if (preview) {
        URL.revokeObjectURL(preview)
      }
    },
    [preview],
  )
  const validation =
    imageError ||
    (fields.some(
      (field) =>
        !field.readOnly && !field.optional && !values[field.name]?.trim(),
    )
      ? "Complete the required fields."
      : null)
  const changed =
    image !== null ||
    fields.some(
      (field) => !field.readOnly && values[field.name] !== field.value,
    )
  const blocked = busy || disabled
  return (
    <form
      className="space-y-6 rounded-md border border-coval-border bg-surface-default p-5"
      onSubmit={(event) => {
        event.preventDefault()
        setShowValidation(true)
        if (blocked || !changed || validation) {
          return
        }
        void onSave(values, image?.file).then((saved) => {
          if (saved && mounted.current) {
            onCancel()
          }
        })
      }}
    >
      <h3 className="text-base font-semibold">Update profile</h3>
      <div className="space-y-2">
        <Label htmlFor={`${id}-image`}>{imageLabel}</Label>
        <div className="flex flex-wrap items-center gap-4">
          <Avatar
            size="lg"
            className={
              imageLabel === "Logo" ? "h-16 w-16 rounded-md" : "h-16 w-16"
            }
            name={name}
            src={image ? image.preview : imageUrl}
          />
          <div className="space-y-2">
            <div className="flex gap-2">
              <Button
                type="button"
                variant="secondary"
                disabled={blocked}
                onClick={() => upload.current?.click()}
              >
                Upload
              </Button>
              {(image ? image.file !== null : hasImage) && (
                <Button
                  type="button"
                  variant="ghost"
                  className="text-coval-danger-text"
                  disabled={blocked}
                  onClick={() => {
                    setImage({ file: null })
                    setImageError(null)
                  }}
                >
                  Remove
                </Button>
              )}
            </div>
            <p className="text-xs text-coval-text-muted">
              Recommended size 1:1, up to 10MB.
            </p>
          </div>
        </div>
        <Input
          ref={upload}
          id={`${id}-image`}
          className="sr-only"
          tabIndex={-1}
          type="file"
          accept="image/*"
          disabled={blocked}
          aria-invalid={!!imageError}
          aria-describedby={imageError ? `${id}-error` : undefined}
          onChange={(event) => {
            const file = event.target.files?.[0]
            event.target.value = ""
            if (!file) {
              return
            }
            if (
              (file.type && !file.type.startsWith("image/")) ||
              file.size > 10 * 1024 * 1024
            ) {
              setImageError("Choose an image up to 10MB.")
              return
            }
            setImageError(null)
            setImage({ file, preview: URL.createObjectURL(file) })
          }}
        />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        {fields.map((field) => (
          <div
            key={field.name}
            className={columns ? "space-y-2" : "space-y-2 sm:col-span-2"}
          >
            <Label htmlFor={`${id}-${field.name}`}>{field.label}</Label>
            <Input
              id={`${id}-${field.name}`}
              value={values[field.name] ?? ""}
              autoComplete={field.autoComplete ?? "off"}
              disabled={blocked || field.readOnly}
              readOnly={field.readOnly}
              aria-invalid={
                showValidation &&
                !field.readOnly &&
                !field.optional &&
                !values[field.name]?.trim()
              }
              aria-describedby={
                showValidation && validation ? `${id}-error` : undefined
              }
              onChange={(event) =>
                setValues((current) => ({
                  ...current,
                  [field.name]: event.target.value,
                }))
              }
            />
          </div>
        ))}
      </div>
      {(imageError || (showValidation && validation)) && (
        <p
          id={`${id}-error`}
          role="alert"
          className="text-sm text-coval-danger-text"
        >
          {validation}
        </p>
      )}
      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          disabled={busy}
          onClick={onCancel}
        >
          Cancel
        </Button>
        <SaveResourceButton
          resourceName="profile"
          type="submit"
          loading={busy}
          disabled={disabled || !changed}
          validationIssues={[validation]}
          onValidationAttempt={() => setShowValidation(true)}
        />
      </div>
    </form>
  )
}
